import { useQuery } from '@tanstack/react-query'
import { TrendingUp, Activity, DollarSign, BarChart3, Target, Brain } from 'lucide-react'
import { analyticsApi } from '../lib/api'
import { formatCurrency, formatPercent } from '../lib/utils'

export default function Analytics() {
  const { data: stats, isLoading } = useQuery({
    queryKey: ['analytics'],
    queryFn: () => analyticsApi.getStats().then(r => r.data),
  })

  const { data: performance } = useQuery({
    queryKey: ['performance'],
    queryFn: () => analyticsApi.getPerformance().then(r => r.data),
  })

  if (isLoading) {
    return <div className="text-center py-12">Loading analytics...</div>
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
        <p className="mt-1 text-gray-500">Performance metrics and insights</p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center">
            <DollarSign className="w-8 h-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm text-gray-500">Total PnL</p>
              <p className={`text-2xl font-bold ${(stats?.totalPnL || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(stats?.totalPnL || 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center">
            <Target className="w-8 h-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm text-gray-500">Win Rate</p>
              <p className="text-2xl font-bold">
                {((stats?.winRate || 0) * 100).toFixed(1)}%
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center">
            <BarChart3 className="w-8 h-8 text-purple-600" />
            <div className="ml-4">
              <p className="text-sm text-gray-500">Sharpe Ratio</p>
              <p className="text-2xl font-bold">
                {(stats?.sharpeRatio || 0).toFixed(2)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center">
            <Activity className="w-8 h-8 text-orange-600" />
            <div className="ml-4">
              <p className="text-sm text-gray-500">Total Trades</p>
              <p className="text-2xl font-bold">{stats?.totalTrades || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* AI Performance */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <Brain className="w-6 h-6 text-blue-600 mr-2" />
            <h2 className="text-lg font-semibold">AI Signal Performance</h2>
          </div>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-4 bg-blue-50 rounded-lg">
              <span className="text-gray-700">Signals Generated</span>
              <span className="font-bold text-xl">{stats?.aiSignals || 0}</span>
            </div>
            <div className="flex justify-between items-center p-4 bg-green-50 rounded-lg">
              <span className="text-gray-700">Profitable Signals</span>
              <span className="font-bold text-xl text-green-600">
                {stats?.profitableSignals || 0}
              </span>
            </div>
            <div className="flex justify-between items-center p-4 bg-purple-50 rounded-lg">
              <span className="text-gray-700">Avg Signal Accuracy</span>
              <span className="font-bold text-xl">
                {((stats?.signalAccuracy || 0) * 100).toFixed(1)}%
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <TrendingUp className="w-6 h-6 text-green-600 mr-2" />
            <h2 className="text-lg font-semibold">Trading Performance</h2>
          </div>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
              <span className="text-gray-700">Avg Trade Size</span>
              <span className="font-bold">{formatCurrency(stats?.avgTradeSize || 0)}</span>
            </div>
            <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
              <span className="text-gray-700">Largest Win</span>
              <span className="font-bold text-green-600">
                +{formatCurrency(stats?.largestWin || 0)}
              </span>
            </div>
            <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
              <span className="text-gray-700">Largest Loss</span>
              <span className="font-bold text-red-600">
                {formatCurrency(stats?.largestLoss || 0)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Chart Placeholder */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold mb-4">Performance Over Time</h2>
        <div className="h-64 bg-gray-50 rounded-lg flex items-center justify-center">
          {performance?.history?.length > 0 ? (
            <div className="w-full h-full p-4">
              {/* Simple bar chart visualization */}
              <div className="flex items-end justify-between h-full space-x-2">
                {performance.history.map((point: any, idx: number) => {
                  const height = Math.min(100, Math.max(5, (point.pnl / (stats?.totalPnL || 1)) * 100 + 50))
                  return (
                    <div
                      key={idx}
                      className="flex-1 flex flex-col items-center"
                    >
                      <div
                        className={`w-full rounded-t ${point.pnl >= 0 ? 'bg-green-400' : 'bg-red-400'}`}
                        style={{ height: `${height}%` }}
                      />
                      <span className="text-xs text-gray-500 mt-1">
                        {new Date(point.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          ) : (
            <p className="text-gray-500">No performance data available yet</p>
          )}
        </div>
      </div>

      {/* Top Performing Markets */}
      {stats?.topMarkets?.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold">Top Performing Markets</h2>
          </div>
          <div className="divide-y divide-gray-200">
            {stats.topMarkets.map((market: any) => (
              <div key={market.id} className="px-6 py-4 flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">{market.question}</p>
                  <p className="text-sm text-gray-500">{market.category}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-green-600">+{formatCurrency(market.pnl)}</p>
                  <p className="text-sm text-gray-500">{market.trades} trades</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Formula Explanations */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl p-6 text-white">
        <h2 className="text-xl font-bold mb-4">The Mathematics Behind Our AI</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white/10 rounded-lg p-4">
            <h3 className="font-semibold mb-2">Expected Value</h3>
            <p className="text-sm text-blue-100">
              We only take trades with positive EV: where the probability of winning 
              multiplied by payout exceeds the probability of losing multiplied by cost.
            </p>
          </div>
          <div className="bg-white/10 rounded-lg p-4">
            <h3 className="font-semibold mb-2">Kelly Criterion</h3>
            <p className="text-sm text-blue-100">
              Position sizing based on edge and bankroll. We use Quarter-Kelly 
              to balance growth with risk management.
            </p>
          </div>
          <div className="bg-white/10 rounded-lg p-4">
            <h3 className="font-semibold mb-2">Sharpe Ratio</h3>
            <p className="text-sm text-blue-100">
              Risk-adjusted returns. We filter for opportunities with Sharpe {'>'} 1.5,
              ensuring quality over quantity.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
